import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  API_URL,
  getAuthHeaders,
  speakMessage,
  MANUAL_CHECK_REASONS,
  feetToMeters,
  formatTime,
  formatDateTime,
} from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckpointMap } from "../components/Map/CheckpointMap";
import {
  Shield,
  LogOut,
  Play,
  Square,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  Navigation,
  Clock,
  Coffee,
  LogIn,
  Loader2,
  QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

export const Patrol = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [currentCheckpointIndex, setCurrentCheckpointIndex] = useState(0);
  const [completedCheckpoints, setCompletedCheckpoints] = useState([]);
  const [guardPosition, setGuardPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isManualDialogOpen, setIsManualDialogOpen] = useState(false);
  const [manualReason, setManualReason] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [checkingIn, setCheckingIn] = useState(false);
  const [todayWorkday, setTodayWorkday] = useState(null);
  const [punchLoading, setPunchLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const watchId = useRef(null);
  
  // Estados para la cámara
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [pendingCheckIn, setPendingCheckIn] = useState(null); // Guarda los datos del check-in pendiente
  const [isQrScannerOpen, setIsQrScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const qrScannerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [routesRes, sessionRes, workdayRes] = await Promise.all([
        axios.get(`${API_URL}/routes/assigned`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/patrol/sessions/active`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/time-records/today`, { headers: getAuthHeaders() }),
      ]);
      const routes = Array.isArray(routesRes.data) ? routesRes.data : [];
      setAssignedRoutes(routes);
      setTodayWorkday(workdayRes.data);
      
      if (sessionRes.data) {
        setActiveSession(sessionRes.data);
        const route = routes.find((r) => r.id === sessionRes.data.route_id);
        if (route) {
          setSelectedRoute(route);
          await loadRouteCheckpoints(route);
          setCompletedCheckpoints(sessionRes.data.completed_checkpoints || []);
          setCurrentCheckpointIndex(sessionRes.data.current_checkpoint_index || 0);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRouteCheckpoints = async (route) => {
    try {
      if (!route || !route.checkpoint_ids || !Array.isArray(route.checkpoint_ids)) {
        setCheckpoints([]);
        return;
      }
      
      const response = await axios.get(`${API_URL}/checkpoints`, {
        headers: getAuthHeaders(),
      });
      
      const allCheckpoints = Array.isArray(response.data) ? response.data : [];
      
      // Crear un mapa para acceso rápido
      const checkpointMap = new Map(allCheckpoints.map(cp => [cp.id, cp]));
      
      let routeCheckpoints = [];
      
      if (route.order_type === "random") {
        // Para aleatorio, filtrar y luego mezclar
        routeCheckpoints = route.checkpoint_ids
          .map((id) => checkpointMap.get(id))
          .filter(Boolean);
        routeCheckpoints = routeCheckpoints.sort(() => Math.random() - 0.5);
      } else {
        // Para secuencial, respetar EXACTAMENTE el orden de checkpoint_ids
        routeCheckpoints = route.checkpoint_ids
          .map((id) => checkpointMap.get(id))
          .filter(Boolean); // Filtrar cualquier checkpoint que no exista
      }
      
      console.log("Loaded checkpoints in order:", routeCheckpoints.map(cp => cp.name).join(" -> "));
      setCheckpoints(routeCheckpoints);
    } catch (error) {
      console.error("Error loading checkpoints:", error);
      toast.error("Error al cargar puntos de control");
      setCheckpoints([]);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Función para obtener ubicación inicial
    const getInitialLocation = () => {
      if (!navigator.geolocation) {
        // Si no hay geolocalización, intentar con IP
        fetch("https://ipapi.co/json/")
          .then(response => response.json())
          .then(data => {
            if (data.latitude && data.longitude) {
              setGuardPosition({
                latitude: parseFloat(data.latitude),
                longitude: parseFloat(data.longitude),
              });
            }
          })
          .catch(() => {
            // Si falla IP, usar ubicación por defecto
            setGuardPosition({
              latitude: 18.4861,
              longitude: -69.9312,
            });
          });
        return;
      }

      // Intentar obtener ubicación inicial con getCurrentPosition
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGuardPosition({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.log("Initial GPS failed, trying less strict:", error);
          // Intentar con configuración menos estricta
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setGuardPosition({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            },
            async (retryError) => {
              console.log("Retry GPS failed, trying IP:", retryError);
              // Intentar con IP
              try {
                const response = await fetch("https://ipapi.co/json/");
                const data = await response.json();
                if (data.latitude && data.longitude) {
                  setGuardPosition({
                    latitude: parseFloat(data.latitude),
                    longitude: parseFloat(data.longitude),
                  });
                } else {
                  // Usar ubicación por defecto
                  setGuardPosition({
                    latitude: 18.4861,
                    longitude: -69.9312,
                  });
                }
              } catch (ipError) {
                // Usar ubicación por defecto
                setGuardPosition({
                  latitude: 18.4861,
                  longitude: -69.9312,
                });
              }
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
          );
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    };

    // Obtener ubicación inicial
    getInitialLocation();
    
    // Luego iniciar watchPosition para actualizaciones continuas
    if (navigator.geolocation) {
      watchId.current = navigator.geolocation.watchPosition(
        (position) => {
          setGuardPosition({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          // Si watchPosition falla, no hacer nada (ya tenemos ubicación inicial)
          console.error("GPS watch error:", error);
        },
        { enableHighAccuracy: false, maximumAge: 10000 }
      );
    }

    return () => {
      if (watchId.current) {
        navigator.geolocation.clearWatch(watchId.current);
      }
      // Limpiar la cámara
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      // Limpiar el escáner QR
      stopQrScanner();
    };
  }, [fetchData]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handlePunch = async (punchType) => {
    // Validación previa en el frontend
    if (punchType === "clock_out" && todayWorkday?.clock_out) {
      toast.error("Ya registraste salida en este turno");
      return;
    }
    if (punchType === "lunch_out" && todayWorkday?.lunch_out) {
      toast.error("Ya registraste salida a almuerzo");
      return;
    }
    if (punchType === "lunch_in" && todayWorkday?.lunch_in) {
      toast.error("Ya registraste entrada de almuerzo");
      return;
    }
    if (punchType === "clock_in" && todayWorkday?.clock_in && !todayWorkday?.clock_out) {
      toast.error("Ya registraste entrada en este turno. Debes registrar salida primero.");
      return;
    }

    // Si no hay ubicación GPS, usar ubicación por defecto
    const finalPosition = guardPosition || {
      latitude: 18.4861, // Santo Domingo, República Dominicana
      longitude: -69.9312,
    };

    setPunchLoading(true);
    try {
      await axios.post(
        `${API_URL}/time-records/punch`,
        {
          punch_type: punchType,
          latitude: finalPosition.latitude,
          longitude: finalPosition.longitude,
        },
        { headers: getAuthHeaders() }
      );

      const messages = {
        clock_in: "Entrada registrada correctamente",
        lunch_out: "Salida a almuerzo registrada",
        lunch_in: "Regreso de almuerzo registrado",
        clock_out: "Salida registrada. ¡Buen trabajo!",
      };
      
      toast.success(messages[punchType]);
      speakMessage(messages[punchType]);
      
      // Small delay to ensure backend has updated the workday
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Refresh workday data
      const workdayRes = await axios.get(`${API_URL}/time-records/today`, {
        headers: getAuthHeaders(),
      });
      
      // Ensure clock_in is set correctly
      const updatedWorkday = workdayRes.data;
      if (punchType === "clock_in") {
        if (!updatedWorkday || !updatedWorkday.clock_in) {
          // If backend didn't return clock_in, set it manually
          const now = new Date().toISOString();
          setTodayWorkday({
            ...updatedWorkday,
            clock_in: now,
            user_id: user?.id,
            company_id: user?.company_id,
            date: new Date().toISOString().split('T')[0],
            total_hours: 0,
            is_complete: false
          });
        } else {
          setTodayWorkday(updatedWorkday);
        }
      } else {
        setTodayWorkday(updatedWorkday);
      }
      
      // If clock_in, reload routes and data to show patrol section
      if (punchType === "clock_in") {
        try {
          const routesRes = await axios.get(`${API_URL}/routes/assigned`, { 
            headers: getAuthHeaders() 
          });
          const routes = Array.isArray(routesRes.data) ? routesRes.data : [];
          setAssignedRoutes(routes);
          
          if (routes.length > 0) {
            // Auto-select first route if only one, or if none selected
            if (routes.length === 1 || !selectedRoute) {
              const firstRoute = routes[0];
              setSelectedRoute(firstRoute);
              await loadRouteCheckpoints(firstRoute);
            }
            
            speakMessage(`Tienes ${routes.length} ruta${routes.length > 1 ? 's' : ''} asignada${routes.length > 1 ? 's' : ''}. Selecciona una para comenzar.`);
            toast.info(`Tienes ${routes.length} ruta${routes.length > 1 ? 's' : ''} asignada${routes.length > 1 ? 's' : ''}`);
          } else {
            toast.info("No tienes rutas asignadas para patrullar");
          }
        } catch (error) {
          console.error("Error loading routes:", error);
          toast.error("Error al cargar rutas asignadas");
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Error al registrar";
      toast.error(errorMessage);
      
      // Si el error es que ya se registró, refrescar el workday para actualizar el estado
      if (errorMessage.includes("Ya registraste")) {
        try {
          const workdayRes = await axios.get(`${API_URL}/time-records/today`, {
            headers: getAuthHeaders(),
          });
          setTodayWorkday(workdayRes.data);
        } catch (refreshError) {
          console.error("Error refreshing workday:", refreshError);
        }
      }
    } finally {
      setPunchLoading(false);
    }
  };

  const startPatrol = async () => {
    if (!selectedRoute) {
      toast.error("Selecciona una ruta primero");
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/patrol/sessions`,
        { route_id: selectedRoute.id },
        { headers: getAuthHeaders() }
      );
      setActiveSession(response.data);
      await loadRouteCheckpoints(selectedRoute);
      setCurrentCheckpointIndex(0);
      setCompletedCheckpoints([]);
      speakMessage("Patrullaje iniciado. Dirígete al primer punto de control.");
      toast.success("Patrullaje iniciado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar patrullaje");
    }
  };

  const endPatrol = async () => {
    if (!activeSession) return;

    try {
      await axios.post(
        `${API_URL}/patrol/sessions/${activeSession.id}/end`,
        {},
        { headers: getAuthHeaders() }
      );
      setActiveSession(null);
      setCheckpoints([]);
      setCurrentCheckpointIndex(0);
      setCompletedCheckpoints([]);
      speakMessage("Patrullaje finalizado. Buen trabajo.");
      toast.success("Patrullaje finalizado");
    } catch (error) {
      toast.error("Error al finalizar patrullaje");
    }
  };

  const checkIn = async (isManual = false, qrCode = null) => {
    if (checkingIn) return;
    
    const currentCheckpoint = checkpoints[currentCheckpointIndex];
    if (!currentCheckpoint) return;

    // Si no hay ubicación GPS y no es manual ni QR, usar ubicación del checkpoint o por defecto
    const finalPosition = guardPosition || {
      latitude: currentCheckpoint.latitude || 18.4861,
      longitude: currentCheckpoint.longitude || -69.9312,
    };

    if (!isManual && !qrCode && !guardPosition) {
      // No mostrar error, usar ubicación del checkpoint o por defecto
      console.log("No hay GPS, usando ubicación del checkpoint o por defecto");
    }

    if (isManual && !manualReason) {
      toast.error("Selecciona una razón para el registro manual");
      return;
    }

    // Si es registro manual, solicitar foto
    if (isManual) {
      // Guardar datos pendientes y abrir diálogo de foto
      setPendingCheckIn({
        isManual,
        checkpoint: currentCheckpoint,
        latitude: guardPosition?.latitude || currentCheckpoint.latitude,
        longitude: guardPosition?.longitude || currentCheckpoint.longitude,
        qrCode: null,
      });
      setIsPhotoDialogOpen(true);
      startCamera();
    } else if (qrCode) {
      // Si es registro por QR, registrar directamente sin foto ni validación de distancia
      await registerCheckpointWithQr(currentCheckpoint, qrCode);
    } else {
      // Si es registro normal, registrar directamente sin foto
      await registerCheckpointDirectly(currentCheckpoint, finalPosition.latitude, finalPosition.longitude);
    }
  };

  const registerCheckpointDirectly = async (checkpoint, latitude, longitude) => {
    if (checkingIn) return;

    setCheckingIn(true);

    try {
      await axios.post(
        `${API_URL}/patrol/check-in`,
        {
          checkpoint_id: checkpoint.id,
          route_id: selectedRoute.id,
          latitude: latitude,
          longitude: longitude,
          is_manual: false,
          manual_reason: null,
          manual_notes: null,
          photo_base64: null, // No foto en registro normal
          qr_code: null,
        },
        { headers: getAuthHeaders() }
      );

      setCompletedCheckpoints([...completedCheckpoints, checkpoint.id]);
      
      const nextIndex = currentCheckpointIndex + 1;
      if (nextIndex < checkpoints.length) {
        setCurrentCheckpointIndex(nextIndex);
        speakMessage(`Punto ${checkpoint.name} registrado. Continúa al siguiente punto.`);
        toast.success(`Punto "${checkpoint.name}" registrado`);
      } else {
        speakMessage("Felicidades. Has completado todos los puntos de la ruta.");
        toast.success("¡Ruta completada!");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al registrar";
      toast.error(message);
      speakMessage("Error al registrar punto. " + message);
    } finally {
      setCheckingIn(false);
    }
  };

  const registerCheckpointWithQr = useCallback(async (checkpoint, qrCode) => {
    if (checkingIn) return;
    
    setCheckingIn(true);

    try {
      // Obtener ubicación actual si está disponible
      const latitude = guardPosition?.latitude || checkpoint.latitude;
      const longitude = guardPosition?.longitude || checkpoint.longitude;

      await axios.post(
        `${API_URL}/patrol/check-in`,
        {
          checkpoint_id: checkpoint.id,
          route_id: selectedRoute.id,
          latitude: latitude,
          longitude: longitude,
          is_manual: false,
          manual_reason: null,
          manual_notes: null,
          photo_base64: null,
          qr_code: qrCode,
        },
        { headers: getAuthHeaders() }
      );

      setCompletedCheckpoints(prev => [...prev, checkpoint.id]);
      
      const nextIndex = currentCheckpointIndex + 1;
      if (nextIndex < checkpoints.length) {
        setCurrentCheckpointIndex(nextIndex);
        speakMessage(`Punto ${checkpoint.name} registrado mediante QR. Continúa al siguiente punto.`);
        toast.success(`Punto "${checkpoint.name}" registrado mediante QR`);
      } else {
        speakMessage("Felicidades. Has completado todos los puntos de la ruta.");
        toast.success("¡Ruta completada!");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al registrar";
      toast.error(message);
      speakMessage("Error al registrar punto. " + message);
    } finally {
      setCheckingIn(false);
      setIsQrScannerOpen(false);
      if (qrScannerRef.current) {
        qrScannerRef.current.stop().then(() => {
          qrScannerRef.current.clear();
          qrScannerRef.current = null;
          setScanning(false);
        }).catch(() => {
          qrScannerRef.current = null;
          setScanning(false);
        });
      }
    }
  }, [checkingIn, guardPosition, selectedRoute, currentCheckpointIndex, checkpoints]);

  const startCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, // Cámara trasera
        audio: false,
      });
      
      streamRef.current = stream;
      setCapturing(true);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
      
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("Error al acceder a la cámara: " + err.message);
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      toast.error("Video no disponible");
      return;
    }
    
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    
    if (width === 0 || height === 0) {
      toast.error("Espera a que la cámara esté lista");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);
    
    const photoData = canvas.toDataURL("image/jpeg", 0.7);
    setCapturedPhoto(photoData);
    
    // Stop camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCapturing(false);
    toast.success("Foto capturada");
  }, []);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    startCamera();
  }, [startCamera]);

  const startQrScanner = useCallback(async () => {
    try {
      setScanning(true);
      const qrCodeRegionId = "qr-reader";
      qrScannerRef.current = new Html5Qrcode(qrCodeRegionId);
      
      await qrScannerRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText) => {
          // QR escaneado exitosamente
          // Limpiar el texto escaneado (eliminar espacios)
          const cleanedQrCode = decodedText.trim();
          
          // Buscar el checkpoint que tiene este QR code
          const checkpointFromQr = checkpoints.find(cp => cp.qr_code && cp.qr_code.trim() === cleanedQrCode);
          
          if (!checkpointFromQr) {
            // Detener el escáner
            if (qrScannerRef.current) {
              await qrScannerRef.current.stop();
              qrScannerRef.current.clear();
              qrScannerRef.current = null;
              setScanning(false);
            }
            toast.error("Código QR no encontrado en los puntos de control de esta ruta");
            setIsQrScannerOpen(false);
            return;
          }
          
          // Debug: verificar valores
          console.log("QR Escaneado:", cleanedQrCode);
          console.log("Checkpoint encontrado por QR:", checkpointFromQr.name, checkpointFromQr.id);
          console.log("Checkpoint actual esperado (índice):", checkpoints[currentCheckpointIndex]?.name, checkpoints[currentCheckpointIndex]?.id);
          console.log("Índice actual:", currentCheckpointIndex);
          console.log("Checkpoints completados:", completedCheckpoints);
          
          // Validar que el checkpoint encontrado por QR sea el siguiente en la secuencia
          const expectedCheckpoint = checkpoints[currentCheckpointIndex];
          if (expectedCheckpoint && checkpointFromQr.id !== expectedCheckpoint.id) {
            // Detener el escáner
            if (qrScannerRef.current) {
              await qrScannerRef.current.stop();
              qrScannerRef.current.clear();
              qrScannerRef.current = null;
              setScanning(false);
            }
            toast.error(`Debes escanear el QR del punto "${expectedCheckpoint.name}" (siguiente en la secuencia), no el de "${checkpointFromQr.name}"`);
            setIsQrScannerOpen(false);
            return;
          }
          
          // Detener el escáner antes de procesar
          if (qrScannerRef.current) {
            await qrScannerRef.current.stop();
            qrScannerRef.current.clear();
            qrScannerRef.current = null;
            setScanning(false);
          }
          // Registrar el checkpoint con el QR (usar el checkpoint encontrado por QR, no el del índice)
          await registerCheckpointWithQr(checkpointFromQr, cleanedQrCode);
        },
        (errorMessage) => {
          // Ignorar errores de escaneo (solo mostrar si es crítico)
        }
      );
    } catch (err) {
      console.error("Error starting QR scanner:", err);
      toast.error("Error al iniciar el escáner QR: " + err.message);
      setScanning(false);
    }
  }, [checkpoints, currentCheckpointIndex, registerCheckpointWithQr]);

  const stopQrScanner = useCallback(() => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop().then(() => {
        qrScannerRef.current.clear();
        qrScannerRef.current = null;
        setScanning(false);
      }).catch((err) => {
        console.error("Error stopping QR scanner:", err);
        qrScannerRef.current = null;
        setScanning(false);
      });
    }
  }, []);

  useEffect(() => {
    if (isQrScannerOpen) {
      // Pequeño delay para asegurar que el DOM esté listo
      setTimeout(() => {
        startQrScanner();
      }, 100);
    } else {
      stopQrScanner();
    }
    return () => {
      stopQrScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQrScannerOpen]);

  const completeCheckIn = async () => {
    // Solo requerir foto si es registro manual
    if (pendingCheckIn?.isManual && !capturedPhoto) {
      toast.error("Debes capturar una foto del lugar para registro manual");
      return;
    }

    if (!pendingCheckIn) return;

    setCheckingIn(true);

    try {
      await axios.post(
        `${API_URL}/patrol/check-in`,
        {
          checkpoint_id: pendingCheckIn.checkpoint.id,
          route_id: selectedRoute.id,
          latitude: pendingCheckIn.latitude,
          longitude: pendingCheckIn.longitude,
          is_manual: pendingCheckIn.isManual,
          manual_reason: pendingCheckIn.isManual ? manualReason : null,
          manual_notes: pendingCheckIn.isManual ? manualNotes : null,
          photo_base64: pendingCheckIn.isManual ? capturedPhoto : null, // Solo foto si es manual
          qr_code: null,
        },
        { headers: getAuthHeaders() }
      );

      setCompletedCheckpoints([...completedCheckpoints, pendingCheckIn.checkpoint.id]);
      
      const nextIndex = currentCheckpointIndex + 1;
      if (nextIndex < checkpoints.length) {
        setCurrentCheckpointIndex(nextIndex);
        speakMessage(`Punto ${pendingCheckIn.checkpoint.name} registrado. Continúa al siguiente punto.`);
        toast.success(`Punto "${pendingCheckIn.checkpoint.name}" registrado`);
      } else {
        speakMessage("Felicidades. Has completado todos los puntos de la ruta.");
        toast.success("¡Ruta completada!");
      }

      setIsManualDialogOpen(false);
      setIsPhotoDialogOpen(false);
      setManualReason("");
      setManualNotes("");
      setCapturedPhoto(null);
      setPendingCheckIn(null);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al registrar";
      toast.error(message);
      speakMessage("Error al registrar punto. " + message);
    } finally {
      setCheckingIn(false);
    }
  };

  const calculateDistance = () => {
    if (!guardPosition || !checkpoints[currentCheckpointIndex]) return null;
    
    const cp = checkpoints[currentCheckpointIndex];
    const R = 6371000;
    const phi1 = (guardPosition.latitude * Math.PI) / 180;
    const phi2 = (cp.latitude * Math.PI) / 180;
    const deltaPhi = ((cp.latitude - guardPosition.latitude) * Math.PI) / 180;
    const deltaLambda = ((cp.longitude - guardPosition.longitude) * Math.PI) / 180;
    
    const a =
      Math.sin(deltaPhi / 2) ** 2 +
      Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  };

  const distance = calculateDistance();
  const currentCheckpoint = checkpoints[currentCheckpointIndex];
  const isWithinRange = currentCheckpoint && distance !== null
    ? distance <= feetToMeters(currentCheckpoint.radius_feet)
    : false;

  const handleLogout = () => {
    logout();
    navigate("/patrol/login");
  };

  const getWorkdayStatus = () => {
    if (!todayWorkday) return "not_started";
    if (todayWorkday.is_complete) return "completed";
    if (todayWorkday.lunch_out && !todayWorkday.lunch_in) return "on_lunch";
    if (todayWorkday.clock_in && !todayWorkday.clock_out) return "working";
    return "not_started";
  };

  const workdayStatus = getWorkdayStatus();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="patrol-page">
      {/* Header */}
      <header className="bg-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-white font-medium">{user?.name}</p>
            <p className="text-slate-400 text-sm">Vigilante</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={handleLogout}
          data-testid="patrol-logout-btn"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 space-y-4 overflow-y-auto">
        <Tabs defaultValue="ponches" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800 border-slate-700">
            <TabsTrigger value="ponches" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Clock className="h-4 w-4 mr-2" />
              Ponches
            </TabsTrigger>
            <TabsTrigger 
              value="vigilancia" 
              className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
              disabled={!todayWorkday?.clock_in || todayWorkday?.clock_out || workdayStatus === "on_lunch"}
            >
              <Shield className="h-4 w-4 mr-2" />
              Registro de Vigilancia
            </TabsTrigger>
          </TabsList>

          {/* Tab: Ponches */}
          <TabsContent value="ponches" className="space-y-4 mt-4">
            {/* Time Clock Card */}
            <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Control de Tiempo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* User Info and Date */}
            <div className="bg-slate-700 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs">Vigilante</p>
                  <p className="text-white font-medium">{user?.name || "Usuario"}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-xs">Fecha</p>
                  <p className="text-white font-medium">
                    {currentTime.toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-600">
                <p className="text-slate-400 text-xs">Hora Actual</p>
                <p className="text-orange-400 font-mono text-lg font-bold">
                  {currentTime.toLocaleTimeString('es-ES', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit' 
                  })}
                </p>
              </div>
            </div>

            {/* Status Display */}
            <div className={`p-4 rounded-lg ${
              workdayStatus === "completed" ? "bg-green-500/20" :
              workdayStatus === "on_lunch" ? "bg-yellow-500/20" :
              workdayStatus === "working" ? "bg-blue-500/20" :
              "bg-slate-700"
            }`}>
              <div className="text-center">
                <p className={`text-lg font-bold ${
                  workdayStatus === "completed" ? "text-green-400" :
                  workdayStatus === "on_lunch" ? "text-yellow-400" :
                  workdayStatus === "working" ? "text-blue-400" :
                  "text-slate-400"
                }`}>
                  {workdayStatus === "completed" && "✓ Jornada Completada"}
                  {workdayStatus === "on_lunch" && "☕ En Almuerzo"}
                  {workdayStatus === "working" && "🟢 Trabajando"}
                  {workdayStatus === "not_started" && "Sin Entrada Registrada"}
                </p>
                {todayWorkday?.total_hours > 0 && (
                  <p className="text-slate-300 mt-1">
                    Horas trabajadas: {todayWorkday.total_hours.toFixed(2)}h
                  </p>
                )}
                {workdayStatus === "not_started" && (
                  <p className="text-slate-400 text-sm mt-2">
                    Presiona el botón para registrar tu entrada
                  </p>
                )}
              </div>
            </div>

            {/* Time Info */}
            {todayWorkday && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {todayWorkday.clock_in && (
                    <div className="bg-slate-700 rounded p-2">
                      <p className="text-slate-400 text-xs">Entrada</p>
                      <p className="text-white font-mono text-sm">
                        {formatTime(todayWorkday.clock_in)}
                      </p>
                    </div>
                  )}
                  {todayWorkday.lunch_out && (
                    <div className="bg-slate-700 rounded p-2">
                      <p className="text-slate-400 text-xs">Salida Almuerzo</p>
                      <p className="text-white font-mono text-sm">
                        {formatTime(todayWorkday.lunch_out)}
                      </p>
                    </div>
                  )}
                  {todayWorkday.lunch_in && (
                    <div className="bg-slate-700 rounded p-2">
                      <p className="text-slate-400 text-xs">Entrada Almuerzo</p>
                      <p className="text-white font-mono text-sm">
                        {formatTime(todayWorkday.lunch_in)}
                      </p>
                    </div>
                  )}
                  {todayWorkday.clock_out && (
                    <div className="bg-slate-700 rounded p-2">
                      <p className="text-slate-400 text-xs">Salida</p>
                      <p className="text-white font-mono text-sm">
                        {formatTime(todayWorkday.clock_out)}
                      </p>
                    </div>
                  )}
                </div>
                {todayWorkday.clock_in && !todayWorkday.clock_out && (
                  <div className="bg-blue-500/20 rounded p-2 border border-blue-500/30">
                    <p className="text-blue-300 text-xs">Tiempo en turno</p>
                    <p className="text-blue-400 font-mono text-sm font-bold">
                      {(() => {
                        const start = new Date(todayWorkday.clock_in);
                        const now = currentTime;
                        const diff = now - start;
                        const hours = Math.floor(diff / 3600000);
                        const minutes = Math.floor((diff % 3600000) / 60000);
                        return `${hours}h ${minutes}m`;
                      })()}
                    </p>
                  </div>
                )}
                {todayWorkday.total_hours > 0 && (
                  <div className="bg-slate-700 rounded p-2">
                    <p className="text-slate-400 text-xs">Total Horas</p>
                    <p className="text-white font-mono text-sm font-bold">
                      {todayWorkday.total_hours.toFixed(2)}h
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Punch Buttons */}
            <div className="grid grid-cols-2 gap-3">
              {/* Clock In */}
              {!todayWorkday?.clock_in && (
                <Button
                  className="col-span-2 h-14 bg-green-600 hover:bg-green-700 text-lg font-bold"
                  onClick={() => handlePunch("clock_in")}
                  disabled={punchLoading}
                  data-testid="punch-clock-in"
                >
                  {punchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <LogIn className="h-5 w-5 mr-2" />
                      REGISTRAR ENTRADA
                    </>
                  )}
                </Button>
              )}

              {/* Lunch Out */}
              {todayWorkday?.clock_in && !todayWorkday?.lunch_out && !todayWorkday?.clock_out && (
                <Button
                  className="h-12 bg-yellow-600 hover:bg-yellow-700"
                  onClick={() => handlePunch("lunch_out")}
                  disabled={punchLoading}
                  data-testid="punch-lunch-out"
                >
                  {punchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <Coffee className="h-5 w-5 mr-2" />
                      Salir Almuerzo
                    </>
                  )}
                </Button>
              )}

              {/* Lunch In */}
              {todayWorkday?.lunch_out && !todayWorkday?.lunch_in && (
                <Button
                  className="h-12 bg-blue-600 hover:bg-blue-700"
                  onClick={() => handlePunch("lunch_in")}
                  disabled={punchLoading}
                  data-testid="punch-lunch-in"
                >
                  {punchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <LogIn className="h-5 w-5 mr-2" />
                      Regresar Almuerzo
                    </>
                  )}
                </Button>
              )}

              {/* Clock Out */}
              {todayWorkday?.clock_in && !todayWorkday?.clock_out && (
                <Button
                  className={`h-12 bg-red-600 hover:bg-red-700 ${
                    !todayWorkday?.lunch_out ? "col-span-1" : 
                    todayWorkday?.lunch_in ? "col-span-2" : ""
                  }`}
                  onClick={() => handlePunch("clock_out")}
                  disabled={punchLoading || (todayWorkday?.lunch_out && !todayWorkday?.lunch_in)}
                  data-testid="punch-clock-out"
                >
                  {punchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <LogOut className="h-5 w-5 mr-2" />
                      Registrar Salida
                    </>
                  )}
                </Button>
              )}

              {/* Start New Shift - When current shift is completed */}
              {todayWorkday?.clock_out && (
                <Button
                  className="col-span-2 h-14 bg-green-600 hover:bg-green-700 text-lg font-bold"
                  onClick={() => handlePunch("clock_in")}
                  disabled={punchLoading}
                  data-testid="punch-new-shift"
                >
                  {punchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <Play className="h-5 w-5 mr-2" />
                      INICIAR NUEVO TURNO
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Tab: Registro de Vigilancia */}
          <TabsContent value="vigilancia" className="space-y-4 mt-4">
            {/* Patrol Section */}
            {todayWorkday?.clock_in && !todayWorkday?.clock_out && workdayStatus !== "on_lunch" ? (
              <>
                {!activeSession ? (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Iniciar Patrullaje</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {assignedRoutes.length === 0 ? (
                        <p className="text-slate-400 text-center py-4">
                          No tienes rutas asignadas
                        </p>
                      ) : (
                        <>
                          <Select
                            value={selectedRoute?.id || ""}
                            onValueChange={async (value) => {
                              const route = assignedRoutes.find((r) => r.id === value);
                              setSelectedRoute(route);
                              if (route) {
                                await loadRouteCheckpoints(route);
                              }
                            }}
                          >
                            <SelectTrigger
                              className="bg-slate-700 border-slate-600 text-white"
                              data-testid="select-route"
                            >
                              <SelectValue placeholder="Seleccionar ruta..." />
                            </SelectTrigger>
                            <SelectContent>
                              {assignedRoutes.map((route) => (
                                <SelectItem key={route.id} value={route.id}>
                                  {route.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Map with selected route checkpoints */}
                          {selectedRoute && (
                            <Card className="bg-slate-700 border-slate-600 overflow-hidden">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-white text-sm">
                                  Ruta: {selectedRoute.name}
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="p-0">
                                {checkpoints.length > 0 ? (
                                  <CheckpointMap
                                    checkpoints={checkpoints}
                                    selectedCheckpoint={null}
                                    guardPosition={guardPosition}
                                    height="300px"
                                    showRadius={true}
                                    completedCheckpoints={completedCheckpoints}
                                  />
                                ) : (
                                  <div className="h-[300px] flex items-center justify-center bg-slate-800">
                                    <p className="text-slate-400">Cargando puntos de control...</p>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          )}

                          <Button
                            className="w-full h-14 text-lg font-bold bg-orange-500 hover:bg-orange-600"
                            onClick={startPatrol}
                            disabled={!selectedRoute}
                            data-testid="start-patrol-btn"
                          >
                            <Play className="h-6 w-6 mr-2" />
                            INICIAR PATRULLAJE
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Active Patrol Status */}
                    <div className="bg-orange-500 rounded-lg px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white">
                        <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                        <span className="font-medium">Patrullaje Activo</span>
                      </div>
                      <span className="text-white/80 text-sm">
                        {completedCheckpoints.length}/{checkpoints.length} puntos
                      </span>
                    </div>

                    {/* Map */}
                    <Card className="bg-slate-800 border-slate-700 overflow-hidden">
                      <CheckpointMap
                        checkpoints={checkpoints}
                        selectedCheckpoint={currentCheckpoint}
                        guardPosition={guardPosition}
                        height="200px"
                        completedCheckpoints={completedCheckpoints}
                      />
                    </Card>

                    {/* Current Checkpoint */}
                    {currentCheckpoint && currentCheckpointIndex < checkpoints.length ? (
                      <Card className="bg-slate-800 border-slate-700">
                        <CardContent className="p-4 space-y-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                              {currentCheckpointIndex + 1}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-white font-bold text-lg">
                                {currentCheckpoint.name}
                              </h3>
                              <p className="text-slate-400 text-sm">
                                {currentCheckpoint.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Navigation className="h-5 w-5 text-slate-400" />
                              <span className="text-slate-300">Distancia:</span>
                            </div>
                            <span
                              className={`font-mono font-bold ${
                                isWithinRange ? "text-green-400" : "text-orange-400"
                              }`}
                            >
                              {distance !== null ? `${distance.toFixed(1)}m` : "---"}
                            </span>
                          </div>

                          <div
                            className={`p-3 rounded-lg flex items-center gap-2 ${
                              isWithinRange
                                ? "bg-green-500/20 text-green-400"
                                : "bg-orange-500/20 text-orange-400"
                            }`}
                          >
                            {isWithinRange ? (
                              <>
                                <CheckCircle2 className="h-5 w-5" />
                                <span>Dentro del rango</span>
                              </>
                            ) : (
                              <>
                                <AlertTriangle className="h-5 w-5" />
                                <span>
                                  Fuera de rango ({feetToMeters(currentCheckpoint.radius_feet).toFixed(1)}m permitido)
                                </span>
                              </>
                            )}
                          </div>

                          <div className="space-y-3">
                            <Button
                              className={`w-full h-14 text-lg font-bold ${
                                isWithinRange
                                  ? "bg-green-600 hover:bg-green-700"
                                  : "bg-slate-600 cursor-not-allowed"
                              }`}
                              onClick={() => checkIn(false)}
                              disabled={!isWithinRange || checkingIn || currentCheckpointIndex >= checkpoints.length}
                              data-testid="check-in-btn"
                            >
                              {checkingIn ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                              ) : (
                                <>
                                  <MapPin className="h-6 w-6 mr-2" />
                                  REGISTRAR PUNTO
                                </>
                              )}
                            </Button>

                            <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                                className="h-12 border-blue-500 text-blue-500 hover:bg-blue-500/10"
                                onClick={() => setIsQrScannerOpen(true)}
                                disabled={checkingIn || currentCheckpointIndex >= checkpoints.length}
                                data-testid="qr-check-in-btn"
                              >
                                <QrCode className="h-5 w-5 mr-2" />
                                Escanear QR
                              </Button>

                              <Button
                                variant="outline"
                                className="h-12 border-orange-500 text-orange-500 hover:bg-orange-500/10"
                              onClick={() => setIsManualDialogOpen(true)}
                              disabled={checkingIn || currentCheckpointIndex >= checkpoints.length}
                              data-testid="manual-check-in-btn"
                            >
                              <AlertTriangle className="h-5 w-5 mr-2" />
                                Manual
                            </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="bg-slate-800 border-slate-700">
                        <CardContent className="p-6 text-center">
                          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                          <h3 className="text-white text-xl font-bold mb-2">
                            ¡Ruta Completada!
                          </h3>
                          <p className="text-slate-400">
                            Has registrado todos los puntos de control
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    <Button
                      variant="outline"
                      className="w-full h-12 border-red-500 text-red-500 hover:bg-red-500/10"
                      onClick={endPatrol}
                      data-testid="end-patrol-btn"
                    >
                      <Square className="h-5 w-5 mr-2" />
                      Finalizar Patrullaje
                    </Button>
                  </>
                )}
              </>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-bold mb-2">
                    No disponible
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {!todayWorkday?.clock_in 
                      ? "Debes registrar tu entrada primero"
                      : todayWorkday?.clock_out 
                      ? "Tu jornada ha finalizado"
                      : "Debes regresar del almuerzo para patrullar"}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Manual Check-in Dialog */}
      <Dialog open={isManualDialogOpen} onOpenChange={setIsManualDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Registro Manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-orange-500/20 text-orange-400 rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              El registro manual quedará documentado para revisión
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Razón del registro manual *</Label>
              <Select value={manualReason} onValueChange={setManualReason}>
                <SelectTrigger
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="manual-reason-select"
                >
                  <SelectValue placeholder="Seleccionar razón..." />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_CHECK_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Notas adicionales</Label>
              <Textarea
                value={manualNotes}
                onChange={(e) => setManualNotes(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Describe la situación..."
                rows={3}
                data-testid="manual-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsManualDialogOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => checkIn(true)}
              disabled={!manualReason || checkingIn}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-manual-check-in"
            >
              Confirmar Registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <Dialog open={isQrScannerOpen} onOpenChange={(open) => {
        setIsQrScannerOpen(open);
        if (!open) {
          stopQrScanner();
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Escanear Código QR</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative aspect-square bg-slate-900 rounded-lg overflow-hidden">
              <div id="qr-reader" className="w-full h-full"></div>
              {!scanning && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                  <p className="text-slate-400">Iniciando escáner...</p>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-400 text-center">
              Apunta la cámara al código QR del punto de control
            </p>
            <Button
              variant="outline"
              className="w-full border-slate-600 text-slate-300"
              onClick={() => {
                setIsQrScannerOpen(false);
                stopQrScanner();
              }}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Capture Dialog */}
      <Dialog open={isPhotoDialogOpen} onOpenChange={(open) => {
        setIsPhotoDialogOpen(open);
        if (!open && streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          setCapturing(false);
          setCapturedPhoto(null);
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Captura Foto del Lugar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!capturedPhoto ? (
              <>
                {capturing && (
                  <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <Button
                  onClick={capturePhoto}
                  disabled={!capturing}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  📷 Capturar Foto
                </Button>
              </>
            ) : (
              <>
                <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
                  <img
                    src={capturedPhoto}
                    alt="Foto capturada"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={retakePhoto}
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300"
                  >
                    🔄 Retomar
                  </Button>
                  <Button
                    onClick={completeCheckIn}
                    disabled={checkingIn}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "✓ Confirmar"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
